import type { FormEvent } from 'react';
import type { CeoBankAccount, CeoDailyPaymentsSummary } from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  formatDateRu,
  formatMillions,
  formatMoney,
  PAYMENT_CATEGORY_LABELS,
} from '../ceoFormat';
import { DailyPaymentsTable } from './DailyPaymentsTable';
import {
  DailySummarySubtext,
  FilterBar,
  FilterLabel,
  FormActions,
  FormBox,
  FormGrid,
  FormTitle,
  HeaderRow,
  InflowTotalText,
  Input,
  OutflowTotalText,
  Section,
  Select,
  Title,
} from './styles';

interface DailyPaymentsSectionProps {
  dailyData: CeoDailyPaymentsSummary | undefined;
  accounts: CeoBankAccount[];
  can_write: boolean;
  showAddPayment: boolean;
  setShowAddPayment: (v: boolean | ((prev: boolean) => boolean)) => void;
  payAccountId: string;
  setPayAccountId: (v: string) => void;
  payDate: string;
  setPayDate: (v: string) => void;
  payDirection: 'inflow' | 'outflow';
  setPayDirection: (v: 'inflow' | 'outflow') => void;
  payAmount: string;
  setPayAmount: (v: string) => void;
  payCounterparty: string;
  setPayCounterparty: (v: string) => void;
  payPurpose: string;
  setPayPurpose: (v: string) => void;
  payCategory: string;
  setPayCategory: (v: string) => void;
  payDocNo: string;
  setPayDocNo: (v: string) => void;
  onCreatePayment: (e: FormEvent) => void;
  filterDate: string;
  setFilterDate: (v: string) => void;
  filterAccount: string;
  setFilterAccount: (v: string) => void;
  filterDirection: string;
  setFilterDirection: (v: string) => void;
  filterCategory: string;
  setFilterCategory: (v: string) => void;
  onResetFilter: () => void;
}

export function DailyPaymentsSection({
  dailyData,
  accounts,
  can_write,
  showAddPayment,
  setShowAddPayment,
  payAccountId,
  setPayAccountId,
  payDate,
  setPayDate,
  payDirection,
  setPayDirection,
  payAmount,
  setPayAmount,
  payCounterparty,
  setPayCounterparty,
  payPurpose,
  setPayPurpose,
  payCategory,
  setPayCategory,
  payDocNo,
  setPayDocNo,
  onCreatePayment,
  filterDate,
  setFilterDate,
  filterAccount,
  setFilterAccount,
  filterDirection,
  setFilterDirection,
  filterCategory,
  setFilterCategory,
  onResetFilter,
}: DailyPaymentsSectionProps) {
  return (
    <Section>
      <HeaderRow>
        <div>
          <Title>
            Платежи за прошлый операционный день (
            {dailyData ? formatDateRu(dailyData.report_date) : '—'})
          </Title>
          {dailyData && (
            <DailySummarySubtext>
              Входящий остаток: <strong>{formatMillions(dailyData.opening_balance)}</strong> ·
              Поступило за день ({dailyData.inflow_count} п/п):{' '}
              <InflowTotalText>
                +{formatMoney(dailyData.inflow_total)}
              </InflowTotalText>{' '}
              · Списано за день ({dailyData.outflow_count} п/п):{' '}
              <OutflowTotalText>
                −{formatMoney(dailyData.outflow_total)}
              </OutflowTotalText>{' '}
              · Исходящий остаток: <strong>{formatMillions(dailyData.closing_balance)}</strong>
              {dailyData.internal_transfer_count > 0 && (
                <span>
                  {' '}
                  · Межсчетные переводы ({dailyData.internal_transfer_count} п/п):{' '}
                  <strong>{formatMoney(dailyData.internal_transfer_total)}</strong>
                </span>
              )}
            </DailySummarySubtext>
          )}
        </div>
        {can_write && (
          <Button
            $size="sm"
            $variant="secondary"
            onClick={() => setShowAddPayment((v) => !v)}
          >
            + Провести платёж
          </Button>
        )}
      </HeaderRow>

      {can_write && showAddPayment && (
        <FormBox as="form" onSubmit={onCreatePayment}>
          <FormTitle>Добавление платежа в реестр</FormTitle>
          <FormGrid>
            <Select
              value={payAccountId}
              onChange={(e) => setPayAccountId(e.target.value)}
            >
              <option value="">Выберите счёт…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bank_name} ({a.account_number.slice(-6)})
                </option>
              ))}
            </Select>
            <Input
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
              required
            />
            <Select
              value={payDirection}
              onChange={(e) => setPayDirection(e.target.value as 'inflow' | 'outflow')}
            >
              <option value="outflow">Списание (расход)</option>
              <option value="inflow">Поступление (приход)</option>
            </Select>
            <Select
              value={payCategory}
              onChange={(e) => setPayCategory(e.target.value)}
            >
              <option value="">Автоклассификация по назначению</option>
              {Object.entries(PAYMENT_CATEGORY_LABELS).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </Select>
            <Input
              type="number"
              placeholder="Сумма платежа, ₽"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              required
            />
            <Input
              placeholder="Контрагент"
              value={payCounterparty}
              onChange={(e) => setPayCounterparty(e.target.value)}
              required
            />
            <Input
              placeholder="№ п/п (напр. ПП-1890)"
              value={payDocNo}
              onChange={(e) => setPayDocNo(e.target.value)}
            />
            <Input
              placeholder="Назначение платежа (автоклассификация статьи)"
              value={payPurpose}
              onChange={(e) => setPayPurpose(e.target.value)}
              required
            />
          </FormGrid>
          <FormActions>
            <Button $size="sm" type="submit">
              Записать платёж
            </Button>
            <Button
              $size="sm"
              $variant="ghost"
              type="button"
              onClick={() => setShowAddPayment(false)}
            >
              Отмена
            </Button>
          </FormActions>
        </FormBox>
      )}

      <FilterBar>
        <FilterLabel>Фильтр операционного дня:</FilterLabel>
        <Input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          title="Оставьте пустым для автоматического показа прошлого операционного дня"
        />
        <Select
          value={filterAccount}
          onChange={(e) => setFilterAccount(e.target.value)}
        >
          <option value="">Все банковские счета</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.bank_name} · {a.account_number}
            </option>
          ))}
        </Select>
        <Select
          value={filterDirection}
          onChange={(e) => setFilterDirection(e.target.value)}
        >
          <option value="">Все операции (приход + расход)</option>
          <option value="inflow">Только поступления (+)</option>
          <option value="outflow">Только списания (−)</option>
        </Select>
        <Select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="">Все категории ДДС</option>
          {Object.entries(PAYMENT_CATEGORY_LABELS).map(([code, label]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </Select>
        {(filterDate || filterAccount || filterDirection || filterCategory) && (
          <Button
            $size="sm"
            $variant="ghost"
            onClick={onResetFilter}
          >
            Сбросить фильтр (Прошлый день)
          </Button>
        )}
      </FilterBar>

      <DailyPaymentsTable payments={dailyData?.payments ?? []} />
    </Section>
  );
}
